/**
 * ConnectionCheck.jsx
 * The Kreeboom ↔ Meta connection, checked from Meta's side.
 *
 * Every figure here is Meta's answer to a direct question, not Kreeboom's
 * record — because "why can I not see my advert in Meta?" is only answerable
 * by Meta. It shows the four facts that decide where an advert can be found:
 * which profile Kreeboom acts as, which ad accounts that profile holds and who
 * owns them, whether Meta lists Kreeboom's campaigns in the account, and which
 * Page each ad appears as.
 *
 * Read-only. Nothing here changes anything at Meta.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useState } from 'react';
import { api } from '../../services/api.js';
import { Check, Refresh, Warning } from '../Icons.jsx';

export default function ConnectionCheck() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setData(await api.ads.connection());
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div style={{ marginTop: 12 }}>
        <button className="btn btn--sm" onClick={run} disabled={busy}>
          <Refresh /> {busy ? 'Asking Meta…' : 'Check connection with Meta'}
        </button>
        {error && (
          <div className="notice notice--error" style={{ marginTop: 10 }}>
            <Warning />
            <span>{error.message}</span>
          </div>
        )}
      </div>
    );
  }

  if (data.problem) {
    return (
      <div className="notice notice--error" style={{ marginTop: 12 }}>
        <Warning />
        <span>{data.problem}</span>
      </div>
    );
  }

  return (
    <div className="delivery" style={{ marginTop: 12 }}>
      {/* 1. Who */}
      <Section title="Kreeboom acts on Meta as">
        {data.login?.error ? (
          <Bad>{data.login.error}</Bad>
        ) : (
          <Good>
            <strong>{data.login?.name}</strong> <Id>{data.login?.id}</Id>
          </Good>
        )}
        <p className="hint">
          To see Kreeboom&rsquo;s adverts in Ads Manager, you must be signed in to Facebook as this
          same profile, or as someone this ad account has been shared with.
        </p>
      </Section>

      {/* 2. Which ad accounts */}
      <Section title="Ad accounts this profile can use">
        {Array.isArray(data.adAccounts) ? (
          data.adAccounts.map((a) => (
            <div key={a.actId} className="delivery__row">
              <span className="delivery__label">{a.name}</span>
              <span className="delivery__state">
                {a.status === 1 ? 'active' : `status ${a.status}`}
              </span>
              <span className="delivery__note">
                {a.business
                  ? `Owned by business portfolio “${a.business.name}”`
                  : 'Personal ad account — not in any business portfolio'}
              </span>
              <Id>{a.actId}</Id>
            </div>
          ))
        ) : (
          <Bad>{data.adAccounts?.error}</Bad>
        )}
      </Section>

      {/* 3. Does Meta list our campaigns there? */}
      {(data.accounts ?? []).map((acc) => (
        <Section key={acc.actId} title={`Inside ${acc.detail?.name ?? acc.actId}`}>
          {acc.listError ? (
            <Bad>Meta would not list the campaigns: {acc.listError}</Bad>
          ) : (
            <p className="hint">
              Meta lists <strong>{acc.campaignsListed}</strong> campaign
              {acc.campaignsListed === 1 ? '' : 's'} in this ad account.
            </p>
          )}

          {acc.comparison.map((c) => (
            <div key={c.campaignId} className="delivery__row">
              <span className="delivery__label">{c.brand}</span>
              {c.listedByMeta ? (
                <span className="delivery__state delivery__state--ok">
                  <Check size={13} /> listed by Meta
                </span>
              ) : (
                <span className="delivery__state delivery__state--bad">not listed</span>
              )}
              <span className="delivery__note">
                {c.name} · Kreeboom says {c.kreeboomStatus}
              </span>
              <Id>{c.campaignId}</Id>
            </div>
          ))}
        </Section>
      ))}

      {/* 4. Which Page each ad appears as */}
      <Section title="Which Page each ad appears as">
        {(data.adverts ?? []).map((adv) =>
          adv.ads.map((ad, i) => {
            if (ad.error) {
              return (
                <div key={ad.adId} className="delivery__row">
                  <span className="delivery__label">{adv.brand}</span>
                  <span className="delivery__state delivery__state--bad">unreadable</span>
                  <span className="delivery__note">{ad.error}</span>
                  <Id>{ad.adId}</Id>
                </div>
              );
            }

            const pageOk = !ad.expectedPageId || ad.actorPageId === ad.expectedPageId;
            const onInstagram = Boolean(ad.instagramUserId || ad.instagramMediaId);
            const igOk =
              !ad.instagramUserId || !ad.expectedInstagramId || ad.instagramUserId === ad.expectedInstagramId;

            return (
              <div key={ad.adId} className="delivery__row">
                <span className="delivery__label">
                  {adv.brand} {i + 1}
                </span>
                <span
                  className={`delivery__state${pageOk && igOk ? ' delivery__state--ok' : ' delivery__state--bad'}`}
                >
                  {pageOk && igOk ? <Check size={13} /> : null}
                  {pageOk && igOk ? 'correct Page' : 'different Page'}
                </span>
                <span className="delivery__note">
                  Appears as <strong>{ad.actorPageName ?? ad.actorPageId ?? 'unknown'}</strong>
                  {onInstagram ? ' · on Instagram' : ' · on Facebook'}
                  {' · '}
                  {(ad.effectiveStatus ?? '').replaceAll('_', ' ').toLowerCase()}
                  {!pageOk &&
                    ` — expected Page ${ad.expectedPageId}, but Meta has ${ad.actorPageId}`}
                </span>
                <Id>{ad.adId}</Id>
              </div>
            );
          }),
        )}
      </Section>

      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn--sm" onClick={run} disabled={busy}>
          <Refresh /> {busy ? 'Asking Meta…' : 'Check again'}
        </button>
        <button className="btn btn--sm" onClick={() => setData(null)}>
          Close
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontWeight: 650, fontSize: 13.5, marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);

const Good = ({ children }) => (
  <div className="delivery__state delivery__state--ok" style={{ textTransform: 'none' }}>
    <Check size={13} /> {children}
  </div>
);

const Bad = ({ children }) => (
  <div className="delivery__state delivery__state--bad" style={{ textTransform: 'none' }}>
    {children}
  </div>
);

const Id = ({ children }) => <span className="delivery__id">{children}</span>;
